import React, { useState, useEffect, useRef } from 'react';
import confetti from 'canvas-confetti';
import { GameHeaderBar } from './GameHeaderBar';
import { TILE_GRID_CSS } from './gameTileGrid';

const fullAlphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

export const MatchDragLetter = ({ handleClose, type, onGameComplete }) => {
  const [remainingLetters, setRemainingLetters] = useState([...fullAlphabet]);
  const [targetLetter, setTargetLetter] = useState('');
  const [isUpperCase, setIsUpperCase] = useState(true);
  const [draggedLetter, setDraggedLetter] = useState(null);
  const [correctCount, setCorrectCount] = useState(0);
  const [celebrated, setCelebrated] = useState(false);
  const [scoreAnim, setScoreAnim] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [letterAnim, setLetterAnim] = useState({}); // {a: 'pulse'|'shake'|undefined}

  // Audio refs
  const correctSoundRef = useRef(null);
  const errorSoundRef = useRef(null);

  useEffect(() => {
    correctSoundRef.current = new Audio('/sounds/correct.mp3');
    errorSoundRef.current = new Audio('/sounds/error.mp3');
    const savedVolume = localStorage.getItem('tapGameVolume');
    if (savedVolume !== null) setVolume(parseFloat(savedVolume));
  }, []);

  // Pick next target letter
  const selectNextLetter = (letters) => {
    const next = type === 'sequence' ? letters[0] : letters[Math.floor(Math.random() * letters.length)];
    setTargetLetter(next);
    setIsUpperCase(Math.random() < 0.5);
  };

  useEffect(() => {
    if (remainingLetters.length > 0) {
      selectNextLetter(remainingLetters);
    }
  }, []);

  useEffect(() => {
    if (correctCount === 26 && !celebrated) {
      confetti({ particleCount: 120, spread: 60, origin: { y: 0.6 } });
      setCelebrated(true);
      // Was missing entirely - the parent's onGameComplete prop (which
      // saves progress via GAME_SAVE, marks the level completed, and
      // shows the "Level Completed!" dialog) was never called here, so
      // finishing this game silently lost the student's progress.
      onGameComplete?.(3, correctCount * 4);
      setTimeout(() => handleClose?.(), 2000);
    }
  }, [correctCount, celebrated, handleClose, onGameComplete]);

  // Speak target letter
  useEffect(() => {
    if (targetLetter && !isMuted) {
      const spoken = isUpperCase ? targetLetter.toUpperCase() : targetLetter.toLowerCase();
      const utterance = new SpeechSynthesisUtterance(`Drag the matching letter: ${spoken}`);
      utterance.rate = 0.8;
      utterance.volume = volume;
      speechSynthesis.speak(utterance);
    }
  }, [targetLetter, isUpperCase, isMuted, volume]);

  const handleDragStart = (letter) => {
    setDraggedLetter(letter);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const expected = isUpperCase ? targetLetter.toLowerCase() : targetLetter.toUpperCase();
    if (draggedLetter === expected) {
      if (!isMuted && correctSoundRef.current) {
        correctSoundRef.current.volume = volume;
        correctSoundRef.current.play();
      }
      // Speak "Correct!" feedback
      if (!isMuted) {
        const utter = new SpeechSynthesisUtterance('Correct!');
        utter.volume = volume;
        speechSynthesis.speak(utter);
      }
      setLetterAnim(prev => ({ ...prev, [draggedLetter]: 'pulse' }));
      setTimeout(() => setLetterAnim(prev => ({ ...prev, [draggedLetter]: undefined })), 700);

      setScoreAnim(true);
      setTimeout(() => setScoreAnim(false), 600);
      const updatedLetters = remainingLetters.filter(l => l !== targetLetter);
      setRemainingLetters(updatedLetters);
      setCorrectCount(prev => prev + 1);
      if (updatedLetters.length > 0) {
        setTimeout(() => selectNextLetter(updatedLetters), 500);
      }
    } else {
      if (!isMuted && errorSoundRef.current) {
        errorSoundRef.current.volume = volume;
        errorSoundRef.current.play();
      }
      // Speak "Try again!" feedback
      if (!isMuted) {
        const utter = new SpeechSynthesisUtterance('Try again!');
        utter.volume = volume;
        speechSynthesis.speak(utter);
      }
      setLetterAnim(prev => ({ ...prev, [draggedLetter]: 'shake' }));
      setTimeout(() => setLetterAnim(prev => ({ ...prev, [draggedLetter]: undefined })), 700);
    }
    setDraggedLetter(null);
  };

  const displayLetter = isUpperCase ? targetLetter.toUpperCase() : targetLetter.toLowerCase();

  // Prepare draggable letter images (opposite case of target)
  const dragLetters = remainingLetters.map(l => {
    const variant = isUpperCase ? l.toLowerCase() : l.toUpperCase();
    return variant;
  });

  return (
    <div className="tap-letter-board-bg tap-board-wide tap-board-md">
      <GameHeaderBar
        score={correctCount}
        total={26}
        questionLabel="Drag the matching letter:"
        question={displayLetter}
        volume={volume}
        onVolumeChange={(v) => {
          setVolume(v);
          localStorage.setItem('tapGameVolume', v);
        }}
        replayTitle="Replay Letter"
        onReplay={() => {
          if (targetLetter && !isMuted) {
            const spoken = isUpperCase ? targetLetter.toUpperCase() : targetLetter.toLowerCase();
            const utterance = new window.SpeechSynthesisUtterance(`Drag the matching letter: ${spoken}`);
            utterance.rate = 0.8;
            utterance.volume = volume;
            window.speechSynthesis.speak(utterance);
          }
        }}
      />

      <div
        onDrop={handleDrop}
        onDragOver={e => e.preventDefault()}
        style={{
          border: '3px dashed #ff9800',
          borderRadius: 16,
          minHeight: 100,
          margin: '4px auto 14px',
          width: 160,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#fffbe7'
        }}
      >
        <span
          style={{
            color: isUpperCase ? '#007bff' : '#43a047',
            fontSize: 48,
            fontWeight: 700,
            borderBottom: '3px solid #ff9800',
            padding: '0 12px'
          }}
        >
          {displayLetter}
        </span>
      </div>

      {/* 9 columns: the 26 letters fit in 3 rows under the drop box. */}
      <div className="tap-letter-btn-grid tap-tile-grid tap-cols-9" style={{ marginTop: 0 }}>
        {dragLetters.map(variant => (
          <div
            key={variant}
            draggable
            onDragStart={() => handleDragStart(variant)}
            className={`letter-btn ${letterAnim[variant] || ''}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'grab',
              opacity: draggedLetter === variant ? 0.5 : 1,
              background: '#fff',
              border: '2px solid #eee',
              borderRadius: 12,
              boxShadow: '0 2px 8px #0001',
              transition: 'transform 0.2s'
            }}
          >
            <img
              src={variant === variant.toUpperCase()
                ? `/letter/${variant}.png`
                : `/lowercase-letter/${variant}.png`
              }
              alt={variant}
              style={{ objectFit: 'contain' }}
            />
          </div>
        ))}
      </div>

      {correctCount === 26 && (
        <h4 className="text-success mt-3" style={{ fontWeight: 700, fontSize: 26 }}>
          🎉 Great job! You matched all the letters!
        </h4>
      )}

      <style>
        {`
          .tap-letter-board-bg {
            background: url('/image/board.png'), linear-gradient(135deg,rgba(62, 62, 62, 0.3) 0%,rgba(15, 15, 14, 0.41) 100%);
            background-size: cover, cover;
            background-position: center, center;
            border-radius: 32px;
            box-shadow: 0 6px 32px #e0c68a33;
            padding: 32px 12px 24px 12px;
            margin: 0 auto;
            max-width: 900px;
            width: 98%;
            min-height: 400px;
            display: flex;
            flex-direction: column;
            align-items: center;
            position: relative;
            z-index: 2;
            animation: board-pop 0.5s cubic-bezier(.68,-0.55,.27,1.55);
          }
          @keyframes board-pop {
            0% { transform: scale(0.9); opacity: 0; }
            100% { transform: scale(1); opacity: 1; }
          }
          .tap-header-bar {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: space-between;
            border-radius: 20px;
            box-shadow: 0 2px 8px rgba(224,198,138,0.10);
            padding: 1px 18px 18px 18px;
            margin-bottom: 18px;
            gap: 0;
          }
          .tap-score-box,
          .tap-volume-box {
            display: flex;
            align-items: center;
            border-radius: 14px;
            padding: 6px 14px;
            font-weight: 700;
            font-size: 1.1rem;
            color: #b8860b;
            background: rgba(255, 255, 255, 0.86);
            min-width: 110px;
            box-shadow: 0 1px 4px rgba(224,198,138,0.10);
          }
          .tap-score-label {
            margin-right: 6px;
            font-size: 1rem;
            color: #b8860b;
          }
          .tap-score-value {
            font-size: 1.1rem;
            color: #1976d2;
            margin-left: 4px;
            font-weight: 700;
          }
          .tap-letter-target {
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(224,198,138,0.92);
            border-radius: 18px;
            padding: 8px 32px;
            box-shadow: 0 1px 4px rgba(224,198,138,0.10);
            margin: 0 18px;
            min-width: 180px;
          }
          .tap-letter-label {
            font-size: 1.1rem;
            color: #3e2723;
            margin-right: 8px;
            font-weight: 600;
          }
          .tap-letter-value {
            font-size: 2rem;
            color: #1976d2;
            font-weight: 900;
            letter-spacing: 2px;
            text-shadow: 1px 1px 8px #fff, 0 0 8px #e0c68a;
          }
          .tap-volume-value {
            margin-left: 8px;
            font-size: 1rem;
            color: #1976d2;
            font-weight: 700;
            min-width: 36px;
            display: inline-block;
            text-align: right;
          }
          .tap-letter-btn-grid {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            align-items: center;
            width: 100%;
            min-height: 220px;
            margin-top: 10px;
            gap: 8px;
          }
          .letter-btn {
            padding: 0;
            border-radius: 18px !important;
            background: linear-gradient(135deg,rgb(250, 248, 240) 0%,rgb(248, 228, 166) 100%) !important;
            box-shadow: 0 2px 8px rgba(0,0,0,0.10);
            border: 2px solid rgba(223, 214, 192, 0.65) !important;
            min-width: 90px;
            min-height: 60px;
            width: 90px;
            height: 60px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: transform 0.1s, box-shadow 0.2s;
          }
          .letter-btn:active, .letter-btn:focus {
            transform: scale(0.96);
            box-shadow: 0 0 0 3px #ffe08255;
          }
          .pulse {
            animation: pulse 0.5s;
          }
          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.12); }
            100% { transform: scale(1); }
          }
          .shake {
            animation: shake 0.4s;
          }
          @keyframes shake {
            0% { transform: translateX(0); }
            20% { transform: translateX(-8px); }
            40% { transform: translateX(8px); }
            60% { transform: translateX(-8px); }
            80% { transform: translateX(8px); }
            100% { transform: translateX(0); }
          }
          .selected-correct,
          .btn-success.selected-correct {
            background: #000000 !important;
            border-color:rgb(0, 245, 253) !important;
            color: #fff !important;
            box-shadow: 0 0 16px 2px rgba(122, 255, 244, 0.6);
            animation: correct-pop 0.5s;
            transparent: none !important;
          }
          @keyframes correct-pop {
            0% { transform: scale(1.15); }
            100% { transform: scale(1); }
          }
          /* Responsive styles */
          @media (max-width: 1024px) {
            .tap-letter-board-bg {
              max-width: 99vw;
              padding: 18px 2vw 18px 2vw;
              min-height: 260px;
            }
            .tap-letter-btn-grid {
              min-height: 120px;
            }
            .letter-btn {
              min-width: 70px;
              min-height: 48px;
              width: 70px;
              height: 48px;
            }
          }
          @media (max-width: 700px) {
            .tap-header-bar {
              flex-direction: column;
              gap: 10px;
              padding-bottom: 10px;
            }
            .tap-letter-target {
              min-width: 0;
              width: 100%;
              padding: 8px 0;
              margin: 8px 0;
            }
            .tap-letter-board-bg {
              padding: 10px 2vw 10px 2vw;
              min-height: 180px;
            }
            .tap-letter-btn-grid {
              min-height: 80px;
              gap: 4px;
            }
            .letter-btn {
              min-width: 48px;
              min-height: 32px;
              width: 48px;
              height: 32px;
            }
          }
          @media (max-width: 480px) {
            .tap-letter-board-bg {
              padding: 6px 1vw 6px 1vw;
              min-height: 120px;
            }
            .tap-header-bar {
              padding: 6px 4px 6px 4px;
            }
            .tap-letter-btn-grid {
              gap: 2px;
            }
            .letter-btn {
              min-width: 36px;
              min-height: 24px;
              width: 36px;
              height: 24px;
            }
          }
        `}
      </style>
      <style>{TILE_GRID_CSS}</style>
    </div>
  );
};